import { httpsCallable } from 'firebase/functions';

import {
  QueryFunnelBody,
  QueryFunnelResponse,
} from '@superfunnel/schemas/analytics-funnel';
import {
  QueryAnalyticsBody,
  QueryAnalyticsResponse,
} from '@superfunnel/schemas/analytics-query';
import {
  PageSpeedAnalyticsBody,
  PageSpeedAnalyticsResponse,
  PageSpeedByPageBody,
  PageSpeedByPageResponse,
} from '@superfunnel/schemas/page-speed-analytics';
import { AnalyticsFilterRule } from '@superfunnel/types/analytics-filter';

import { functions } from '../Firebase';

export async function getAnalyticsByDates(
  accountId: string,
  scope: 'account' | 'campaign',
  dynamicPageId: string | undefined,
  startDate: string,
  endDate: string,
  advancedFilterRules?: AnalyticsFilterRule[],
): Promise<any> {
  const fn = httpsCallable(functions, 'analytics/analyticsByDates');

  const response = await fn({
    accountId,
    scope,
    ...(dynamicPageId ? { campaignId: dynamicPageId } : {}),
    startDate,
    endDate,
    ...(advancedFilterRules?.length ? { advancedFilterRules } : {}),
  });

  return response.data;
}

export async function getWebsiteOnViewAnalytics(
  accountId: string,
  dynamicPageId: string,
  websiteId: string,
  startDate?: string,
  endDate?: string,
): Promise<any> {
  const fn = httpsCallable(functions, 'analytics/websiteOnViewAnalytics');

  const response = await fn({
    accountId,
    ...(dynamicPageId ? { campaignId: dynamicPageId } : {}),
    websiteId,
    startDate,
    endDate,
  });

  return response.data;
}

export interface ExternalAnalyticsDateRange {
  startDate: string;
  endDate: string;
}

export interface ExternalAnalyticsMetricError {
  code: 'deadline-exceeded' | 'report-failed';
  message: string;
}

export type ExternalAnalyticsMetricResult<T> =
  | { status: 'success'; value: T }
  | { status: 'error'; error: ExternalAnalyticsMetricError };

export interface ExternalAnalyticsConversionAction {
  actionName: string;
  conversions: number;
  allConversions: number;
  value?: number;
}

export interface ExternalAnalyticsRangeResult {
  cpc?: ExternalAnalyticsMetricResult<number | null>;
  conversions: ExternalAnalyticsMetricResult<number>;
  cost?: ExternalAnalyticsMetricResult<number>;
  clicks: ExternalAnalyticsMetricResult<number>;
  conversionsValue?: ExternalAnalyticsMetricResult<number>;
  profit?: ExternalAnalyticsMetricResult<number>;
  avgSpeedScore: ExternalAnalyticsMetricResult<number>;
  conversionActions: ExternalAnalyticsMetricResult<
    ExternalAnalyticsConversionAction[]
  >;
  currencyCode: string;
}

export interface ExternalAnalyticsRangesResponse {
  current: ExternalAnalyticsRangeResult | null;
  previous: ExternalAnalyticsRangeResult | null;
}

const EXTERNAL_ANALYTICS_CLIENT_TIMEOUT_MS = 30_000;

export async function getExternalAnalyticsByDateRanges(
  accountId: string,
  dynamicPageId: string,
  ranges: {
    current: ExternalAnalyticsDateRange;
    previous: ExternalAnalyticsDateRange;
  },
): Promise<ExternalAnalyticsRangesResponse> {
  const fn = httpsCallable<
    {
      accountId: string;
      campaignId: string;
      ranges: {
        current: ExternalAnalyticsDateRange;
        previous: ExternalAnalyticsDateRange;
      };
    },
    ExternalAnalyticsRangesResponse
  >(functions, 'analytics/externalAnalyticsByDates', {
    timeout: EXTERNAL_ANALYTICS_CLIENT_TIMEOUT_MS,
  });

  const response = await fn({
    accountId,
    campaignId: dynamicPageId,
    ranges,
  });

  return response.data;
}

export type ExportFormat = 'csv' | 'json';

export async function exportAnalytics(
  accountId: string,
  filter: Record<string, any>,
  metrics: { value: string; title: string }[],
  groupBy: { value: string; title: string }[],
  segments: { value: string; title: string }[],
  startDate: string,
  endDate: string,
  format: ExportFormat = 'csv',
): Promise<{
  ok: boolean;
  jobId: string;
  fileName: string;
}> {
  const fn = httpsCallable<
    {
      accountId: string;
      filter: Record<string, any>;
      metrics: { value: string; title: string }[];
      groupBy: { value: string; title: string }[];
      segments: { value: string; title: string }[];
      startDate: string;
      endDate: string;
      format?: ExportFormat;
    },
    {
      ok: boolean;
      jobId: string;
      fileName: string;
    }
  >(functions, 'analytics/exportAnalytics');

  const response = await fn({
    accountId,
    filter,
    metrics,
    groupBy,
    segments,
    startDate,
    endDate,
    format,
  });

  return response.data;
}

export async function getUsage(
  accountId: string,
  startDate: string,
  endDate: string,
): Promise<{
  dailyVisitors: {
    event: string;
    day: string;
    custom: number;
    totalCustom: number;
    uniqueCustom: number;
  }[];
  totalVisitors: number;
}> {
  const fn = httpsCallable<
    {
      accountId: string;
      startDate: string;
      endDate: string;
    },
    {
      dailyVisitors: {
        event: string;
        day: string;
        custom: number;
        totalCustom: number;
        uniqueCustom: number;
      }[];
      totalVisitors: number;
    }
  >(functions, 'analytics/usage');

  const response = await fn({
    accountId,
    startDate,
    endDate,
  });

  return response.data;
}

export async function getAdsAccountsUsage(
  accountId: string,
  fromDate: string,
  toDate: string,
  aggregateBy: 'date' | 'total' = 'total',
  adsAccountsIds?: string[],
): Promise<
  {
    clicks: number;
    impressions: number;
    cost: number;
    conversions: number;
    isChargeable: boolean;
    adsAccountId: string;
    date?: string;
  }[]
> {
  const fn = httpsCallable<
    {
      accountId: string;
      fromDate: string;
      toDate: string;
      aggregateBy: 'date' | 'total';
      adsAccountsIds?: string[];
    },
    {
      clicks: number;
      impressions: number;
      cost: number;
      conversions: number;
      isChargeable: boolean;
      adsAccountId: string;
      date?: string;
    }[]
  >(functions, 'accounts/billing/getAdsAccountsUsage');

  const response = await fn({
    accountId,
    fromDate,
    toDate,
    aggregateBy,
    adsAccountsIds,
  });

  return response.data;
}

/**
 * Query analytics data with support for batching multiple queries.
 *
 * @example Single query
 * queryAnalytics({
 *   accountId,
 *   startDate: '2025-01-01',
 *   endDate: '2025-01-31',
 *   groupBy: ['day'],
 *   events: ['view', 'conversion']
 * })
 *
 * @example Batch with different date ranges
 * queryAnalytics({
 *   accountId,
 *   groupBy: ['day'],
 *   events: ['view'],
 *   queries: [
 *     { id: 'current', startDate: '2025-01-01', endDate: '2025-01-31' },
 *     { id: 'previous', startDate: '2024-12-01', endDate: '2024-12-31' }
 *   ]
 * })
 */
export async function queryAnalytics(
  params: QueryAnalyticsBody,
): Promise<QueryAnalyticsResponse> {
  const fn = httpsCallable<QueryAnalyticsBody, QueryAnalyticsResponse>(
    functions,
    'analytics/queryAnalytics',
  );

  const response = await fn(params);
  return response.data;
}

/**
 * Fetches first-party RUM (real-user monitoring) page-speed field data: p75
 * Core Web Vitals with per-device breakdown, Google distribution buckets, and
 * optional daily timeseries / worst paths / attribution samples.
 */
export async function getPageSpeedAnalytics(
  params: PageSpeedAnalyticsBody,
): Promise<PageSpeedAnalyticsResponse> {
  const fn = httpsCallable<PageSpeedAnalyticsBody, PageSpeedAnalyticsResponse>(
    functions,
    'analytics/pageSpeedAnalytics',
  );

  const response = await fn(params);
  return response.data;
}

/**
 * Fetches batch p75 Core Web Vitals per dynamic page over a trailing window,
 * used to add performance columns to page/funnel grids.
 */
export async function getPageSpeedByPage(
  params: PageSpeedByPageBody,
): Promise<PageSpeedByPageResponse> {
  const fn = httpsCallable<PageSpeedByPageBody, PageSpeedByPageResponse>(
    functions,
    'analytics/pageSpeedByPage',
  );

  const response = await fn(params);
  return response.data;
}

/**
 * Filter options response type
 */
export interface AnalyticsFilterOptionsResponse {
  os: { value: string; count: number }[];
  browser: { value: string; count: number }[];
  country: { value: string; count: number }[];
  city: { value: string; count: number }[];
  referrerDomain: { value: string; count: number }[];
  origin: { value: string; count: number }[];
  utmSource: { value: string; count: number }[];
  utmMedium: { value: string; count: number }[];
  utmContent: { value: string; count: number }[];
  region: { value: string; count: number }[];
  network: { value: string; count: number }[];
  matchType: { value: string; count: number }[];
  adAccountId: { value: string; count: number }[];
  adCampaignId: { value: string; count: number }[];
  adGroupId: { value: string; count: number }[];
  adId: { value: string; count: number }[];
  keyword: { value: string; count: number }[];
  placement: { value: string; count: number }[];
  path: { value: string; count: number }[];
}

/**
 * Fetches distinct values for filter fields from analytics data.
 * Used to populate the filter builder dropdowns.
 */
export async function getAnalyticsFilterOptions(
  accountId: string,
  startDate: string,
  endDate: string,
): Promise<AnalyticsFilterOptionsResponse> {
  const fn = httpsCallable<
    {
      accountId: string;
      startDate: string;
      endDate: string;
    },
    AnalyticsFilterOptionsResponse
  >(functions, 'analytics/filterOptions');

  const response = await fn({
    accountId,
    startDate,
    endDate,
  });

  return response.data;
}

export interface DetectedCustomEvent {
  eventName: string;
  count: number;
  lastSeenAt: string;
  pages: { dynamicPageId: string; count: number }[];
}

/**
 * Every custom event name the account fired from the SDK or the offline API in
 * the last 30 days, with the pages that fired them, configured or not.
 */
export async function getDetectedCustomEvents(
  accountId: string,
): Promise<DetectedCustomEvent[]> {
  const fn = httpsCallable<
    { accountId: string },
    { events: DetectedCustomEvent[] }
  >(functions, 'analytics/detectedCustomEvents');

  const response = await fn({ accountId });

  return response.data.events;
}

export async function queryFunnel(
  body: QueryFunnelBody,
): Promise<QueryFunnelResponse> {
  const fn = httpsCallable<QueryFunnelBody, QueryFunnelResponse>(
    functions,
    'analytics/funnel',
  );

  const response = await fn(body);

  return response.data;
}
